import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { centerToast } from '../components/CenterToast';
import { ArrowLeft, MapPin, CheckCircle, Circle, Navigation, Users, Heart } from 'lucide-react';
import DonationModal from '../components/DonationModal';
import MapView from '../components/MapView';

const MOCK_FUNERAL_DATA = {
  deceasedName: 'Late Mr. Razak bin Abdullah',
  deceasedPhoto: 'https://images.unsplash.com/photo-1496769336828-c522a3a7e33c?w=300&q=80',
  dateOfPassing: '22 April 2026',
  currentStatus: 'prayer',
  timeline: [
    { id: 'confirm', label: 'Ihsan Casket representative confirmed details', status: 'completed', location: 'Via WhatsApp', time: '8:00 AM - 8:30 AM' },
    { id: 'hospital', label: 'At Hospital', status: 'completed', location: 'Changi General Hospital', time: '9:30 AM - 10:00 AM' },
    { id: 'washing', label: 'Washing/Mandi', status: 'completed', location: 'Blk 41, Level 3, Bedok South Road (S)460041', time: '10:45 AM - 11:15 AM' },
    { id: 'prayer', label: 'Prayer/Solat', status: 'active', location: 'Masjid Al-Istiqamah', time: '12:00 PM - 12:30 PM' },
    { id: 'burial', label: 'Burial at Pusara Aman', status: 'pending', location: 'S1-10/412', estimatedTime: '2:30 PM - 3:00 PM' }
  ],
  burialLocation: {
    name: 'Pusara Aman Cemetery',
    address: '11 Lim Chu Kang Rd, Singapore 719452',
    coordinates: { lat: 1.3634, lng: 103.7143 }
  },
  attendees: 47
};

export default function TrackerView({ shareableLink, bookingData, onBack }) {
  const [isAttending, setIsAttending] = useState(false);
  const [showDonation, setShowDonation] = useState(false);
  const [attendeeCount, setAttendeeCount] = useState(MOCK_FUNERAL_DATA.attendees);
  const funeralData = bookingData || MOCK_FUNERAL_DATA;

  const handleAttendanceToggle = () => {
    setIsAttending(!isAttending);
    setAttendeeCount(prev => isAttending ? prev - 1 : prev + 1);
    centerToast(isAttending ? 'Attendance removed' : 'Marked as attending', isAttending ? '👋' : '✅');
  };

  const handleNavigate = () => {
    const { address } = funeralData.burialLocation;
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    window.open(mapsUrl, '_blank', 'noopener,noreferrer');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-primary';
      case 'active':
        return 'text-accent';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/30 to-background">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={onBack} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <h1 className="text-lg sm:text-xl font-semibold">Service Tracker</h1>
            <div className="w-16"></div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Deceased Info Card */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <Avatar className="h-20 w-20 border-2 border-primary">
                <AvatarImage src={funeralData.deceasedPhoto} alt={funeralData.deceasedName} />
                <AvatarFallback className="text-xl">R</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h2 className="text-2xl sm:text-3xl font-semibold mb-1">{funeralData.deceasedName}</h2>
                <p className="text-muted-foreground">Passed away: {funeralData.dateOfPassing}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{attendeeCount} people attending</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-xl">Service Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {funeralData.timeline.map((step, index) => (
                <div key={step.id} className="relative flex gap-4">
                  {/* Vertical line */}
                  {index < funeralData.timeline.length - 1 && (
                    <div className="absolute left-5 top-12 h-full w-0.5 bg-border" />
                  )}
                  
                  {/* Status indicator */}
                  <div className={`relative z-10 flex-shrink-0 ${getStatusColor(step.status)}`}>
                    {step.status === 'completed' ? (
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-6 w-6" />
                      </div>
                    ) : step.status === 'active' ? (
                      <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center animate-pulse-gentle">
                        <Circle className="h-6 w-6 fill-current" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                        <Circle className="h-6 w-6" />
                      </div>
                    )}
                  </div>

                  {/* Step content */}
                  <div className="flex-1 pb-6">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="text-lg font-semibold mb-1">{step.label}</h3>
                        {step.location && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mb-1">
                            <MapPin className="h-3.5 w-3.5" />
                            {step.location}
                          </p>
                        )}
                        {step.time && (
                          <p className="text-sm text-muted-foreground">{step.time}</p>
                        )}
                        {step.estimatedTime && (
                          <p className="text-sm text-muted-foreground">Est. {step.estimatedTime}</p>
                        )}
                      </div>
                      {step.status === 'active' && (
                        <Badge className="bg-accent text-accent-foreground">En Route</Badge>
                      )}
                      {step.status === 'completed' && (
                        <Badge className="bg-primary/10 text-primary">Completed</Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Map View */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-xl">Live Location</CardTitle>
          </CardHeader>
          <CardContent>
            <MapView 
              destination={funeralData.burialLocation}
              currentStatus={funeralData.currentStatus}
            />
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="grid sm:grid-cols-3 gap-4">
          <Button 
            onClick={handleNavigate}
            size="lg"
            className="h-14 text-base gap-2"
          >
            <Navigation className="h-5 w-5" />
            Navigate to Grave
          </Button>
          
          <Button 
            onClick={handleAttendanceToggle}
            variant={isAttending ? 'default' : 'outline'}
            size="lg"
            className="h-14 text-base gap-2 border-2"
          >
            <Users className="h-5 w-5" />
            {isAttending ? 'Attending' : "I'm Attending"}
          </Button>

          <Button 
            onClick={() => setShowDonation(true)}
            variant="outline"
            size="lg"
            className="h-14 text-base gap-2 border-2 border-accent hover:bg-accent hover:text-accent-foreground"
          >
            <Heart className="h-5 w-5" />
            Give Niat (Donation)
          </Button>
        </div>
      </div>

      {/* Donation Modal */}
      {showDonation && (
        <DonationModal 
          deceasedName={funeralData.deceasedName}
          onClose={() => setShowDonation(false)}
        />
      )}
    </div>
  );
}
